// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY: section 12's formats (English, all in the calendar's timezone): <date> as "Mon, Nov 2" (", y" for
// another year), a card's time ("2:30 – 3:15 PM", "11:00 AM – 1:00 PM", "Tue 7:00 PM – Wed 1:00 AM", "All day"),
// the sheet's day and time ("Tue, Nov 3", "7:00 PM"), the month title ("November 2026") and the day list's
// "Add on <day>" ("Nov 3"). Days are keyed as YYYY-MM-DD in the calendar's timezone. Exclude from upstream sync.

import {fill, CalendarStrings as S} from '@app/features/calendar/CalendarStrings';
import type {CalendarEvent} from '@app/features/calendar/CalendarTypes';
import {DateTime} from 'luxon';

const LOCALE = 'en-US';

export function zoned(ms: number, timezone: string): DateTime {
	return DateTime.fromMillis(ms, {zone: timezone}).setLocale(LOCALE);
}

/** 00:00 of a day in the timezone. */
export function dayStart(dayKey: string, timezone: string): DateTime {
	return DateTime.fromISO(dayKey, {zone: timezone}).setLocale(LOCALE);
}

export function dayKeyOf(ms: number, timezone: string): string {
	return zoned(ms, timezone).toISODate() as string;
}

export function addDays(dayKey: string, days: number): string {
	return DateTime.fromISO(dayKey, {zone: 'UTC'}).plus({days}).toISODate() as string;
}

/** YYYY-MM of a day. */
export function monthKeyOf(dayKey: string): string {
	return dayKey.slice(0, 7);
}

export function addMonths(monthKey: string, months: number): string {
	return DateTime.fromISO(`${monthKey}-01`, {zone: 'UTC'}).plus({months}).toFormat('yyyy-MM');
}

/** UTC ms of a wall-clock time (HH:mm) on a day, in the timezone. */
export function wallClockToMs(dayKey: string, time: string, timezone: string): number {
	return DateTime.fromISO(`${dayKey}T${time}`, {zone: timezone}).toMillis();
}

export function formatDay(dayKey: string, todayKey: string): string {
	const day = DateTime.fromISO(dayKey, {zone: 'UTC'}).setLocale(LOCALE);
	return day.toFormat(dayKey.slice(0, 4) === todayKey.slice(0, 4) ? 'EEE, MMM d' : 'EEE, MMM d, y');
}

/** "Add on <day>"'s day: "Nov 3" ("Jan 4, 2027" for another year). */
export function formatShortDay(dayKey: string, todayKey: string): string {
	const day = DateTime.fromISO(dayKey, {zone: 'UTC'}).setLocale(LOCALE);
	return day.toFormat(dayKey.slice(0, 4) === todayKey.slice(0, 4) ? 'MMM d' : 'MMM d, y');
}

export function formatTime(ms: number, timezone: string): string {
	return zoned(ms, timezone).toFormat('h:mm a');
}

export function formatMonthTitle(monthKey: string): string {
	return DateTime.fromISO(`${monthKey}-01`, {zone: 'UTC'}).setLocale(LOCALE).toFormat('MMMM yyyy');
}

/** "Today · Mon, Nov 2", "Tomorrow · Tue, Nov 3", else the date alone. */
export function dayHeading(dayKey: string, todayKey: string): string {
	const date = formatDay(dayKey, todayKey);
	if (dayKey === todayKey) return fill(S.todayHeading, {date});
	if (dayKey === addDays(todayKey, 1)) return fill(S.tomorrowHeading, {date});
	return date;
}

export function cardTime(event: CalendarEvent, timezone: string): string {
	if (event.all_day || event.start_ms == null || event.end_ms == null) return S.allDay;
	const start = zoned(event.start_ms, timezone);
	const end = zoned(event.end_ms, timezone);
	if (start.toISODate() !== end.toISODate()) {
		return `${start.toFormat('EEE h:mm a')} – ${end.toFormat('EEE h:mm a')}`;
	}
	if (start.toFormat('a') === end.toFormat('a')) {
		return `${start.toFormat('h:mm')} – ${end.toFormat('h:mm a')}`;
	}
	return `${start.toFormat('h:mm a')} – ${end.toFormat('h:mm a')}`;
}

/** The day an event is listed under: its start's day. */
export function eventDayKey(event: CalendarEvent, timezone: string): string {
	if (event.all_day && event.start_date != null) return event.start_date;
	return dayKeyOf(event.start_ms ?? 0, timezone);
}

/** Every day an event covers (an all-day event's span; a timed event's start to end). */
export function eventDayKeys(event: CalendarEvent, timezone: string): Array<string> {
	const first = eventDayKey(event, timezone);
	let last = first;
	if (event.all_day && event.end_date != null) last = addDays(event.end_date, -1);
	else if (!event.all_day && event.end_ms != null)
		last = dayKeyOf(Math.max(event.end_ms - 1, event.start_ms ?? 0), timezone);
	const days: Array<string> = [];
	for (let day = first; day <= last && days.length < 366; day = addDays(day, 1)) days.push(day);
	return days.length > 0 ? days : [first];
}

/** UTC ms the event starts (an all-day event: 00:00 of its first day in the timezone). */
export function eventStartMs(event: CalendarEvent, timezone: string): number {
	if (event.all_day && event.start_date != null) return dayStart(event.start_date, timezone).toMillis();
	return event.start_ms ?? 0;
}

/** UTC ms the event ends (an all-day event: 00:00 of its exclusive end day). */
export function eventEndMs(event: CalendarEvent, timezone: string): number {
	if (event.all_day && event.end_date != null) return dayStart(event.end_date, timezone).toMillis();
	return event.end_ms ?? eventStartMs(event, timezone);
}
